import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';
import { AssessmentSlides } from './components/AssessmentSlides';
import type { UnitAssessmentSlides } from './types';
import { AssessmentSlideEditor } from './components/AssessmentSlideEditor';
import { unitAssessmentState } from './UnitAssessmentState';
import { findIncompleteSlides } from '@duke-hq/svc-api/lib/assessment/incompleteSlides';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { withPermission } from '../withPermission';
import { pageContentPadding } from '@duke-hq/ui/theme/tokens';
import { getSlideIcon } from './unitAssessmentSlides';
import { SlideKind } from '@duke-hq/svc-api-db';
import { EditorLayout } from '../navbar/EditorLayout';
import { EditorStatus } from '../navbar/EditorStatus';
import { EditorBreadcrumbs } from '../navbar/EditorBreadcrumbs';
import { AssessmentPublishButton } from '../navbar/AssessmentPublishButton';
import { ContinueGenerationButton } from '../navbar/ContinueGenerationButton';

interface DraggableItem {
  id: string;
  position: number;
}

function findDraggedItemPositions(
  originalList: DraggableItem[],
  reorderedList: DraggableItem[],
): { originalPosition: number; newPosition: number } | null {
  for (let i = 0; i < reorderedList.length; i++) {
    const originalItem = originalList[i];
    const reorderedItem = reorderedList[i];

    if (!originalItem || !reorderedItem) {
      continue;
    }

    if (originalItem.id !== reorderedItem.id) {
      const draggedDown = originalItem.position + 1 === reorderedItem.position;

      if (draggedDown) {
        const draggedItem = reorderedList.find((item) => item.position === originalItem.position);

        if (!draggedItem) {
          return null;
        }

        const draggedOriginal = originalList[reorderedList.indexOf(draggedItem)];
        if (!draggedOriginal) {
          return null;
        }

        return {
          originalPosition: originalItem.position,
          newPosition: draggedOriginal.position,
        };
      } else {
        return {
          originalPosition: reorderedItem.position,
          newPosition: originalItem.position,
        };
      }
    }
  }

  return null; // No changes detected
}

export const UnitAssessmentPage = withPermission('canManageCourseContent', () => {
  const { unitId } = useParams();
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  if (!unitId) {
    throw new Error('unitId is required');
  }
  const utils = trpcAdmin.useUtils();
  const [assessment, queryResult] = trpcAdmin.unitAssessment.byUnitId.useSuspenseQuery(unitId);
  const isLoading = queryResult.isLoading;
  const unitOfCompetency = assessment.unitOfCompetency;
  const unitAssessmentVersionId = assessment.draftVersion?.id;
  if (unitAssessmentVersionId === null || unitAssessmentVersionId === undefined) {
    throw new Error('unit assessment version is required');
  }
  const getSlidesInput = {
    unitAssessmentVersionId: unitAssessmentVersionId,
  };
  const [{ slides: slidesData, fileUrls }, slidesQueryResult] =
    trpcAdmin.unitAssessment.getSlides.useSuspenseQuery(getSlidesInput);

  unitAssessmentState.setDraftVersionId(assessment.draftVersion?.id ?? null);
  unitAssessmentState.setSlidesLength(slidesData.length);
  unitAssessmentState.setCurrentAssessmentId(assessment.id);

  useEffect(() => {
    const hasUnpublished =
      !assessment.lastPublishedVersion?.publishedAt ||
      (assessment.draftVersion != null &&
        new Date(assessment.draftVersion.updatedAt) >
          new Date(assessment.lastPublishedVersion.publishedAt));
    unitAssessmentState.setHasUnpublishedChanges(hasUnpublished);
  }, [assessment.draftVersion, assessment.lastPublishedVersion?.publishedAt]);

  const incompleteSlides = useMemo(() => findIncompleteSlides(slidesData), [slidesData]);

  useEffect(() => {
    unitAssessmentState.setIncompleteSlides(incompleteSlides);
  }, [incompleteSlides]);

  const [slides, setSlides] = useState<UnitAssessmentSlides>(slidesData);

  useMemo(() => {
    setSlides(slidesData);
  }, [slidesData]);

  const reorderSlides = trpcAdmin.unitAssessment.reorderAssessmentSlides.useMutation({
    onSuccess: () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
    },
    onSettled: () => {
      void utils.unitAssessment.getSlides.invalidate(getSlidesInput);
    },
  });

  const saveReorder = useCallback(() => {
    const newPositions = findDraggedItemPositions(slidesData, slides);
    const draftVersionId = unitAssessmentSnap.draftVersionId;

    if (draftVersionId == null) {
      throw new Error('draftVersionId is required');
    }

    const reorderInput = {
      unitAssessmentVersionId: draftVersionId,
    };
    if (newPositions) {
      reorderSlides.mutate({
        ...reorderInput,
        fromPosition: newPositions.originalPosition,
        toPosition: newPositions.newPosition,
      });
    }
  }, [
    reorderSlides,
    slides,
    slidesData,
    unitAssessmentSnap.currentAssessmentId,
    unitAssessmentSnap.draftVersionId,
  ]);

  const deleteAssessmentSlide = trpcAdmin.unitAssessment.deleteSlide.useMutation({
    onSuccess: async () => {
      if (unitAssessmentSnap.isEditorOpen) {
        unitAssessmentState.closeEditor();
      }
      unitAssessmentState.setHasUnpublishedChanges(true);
      await slidesQueryResult.refetch();
    },
  });

  const handleSlideDelete = (slideId: string) => {
    deleteAssessmentSlide.mutate({ slideId });
  };

  const deleteMultiChoiceQuestion = trpcAdmin.unitAssessment.deleteMultiChoiceQuestion.useMutation({
    onSuccess: async () => {
      if (unitAssessmentSnap.isEditorOpen) {
        unitAssessmentState.closeEditor();
      }
      unitAssessmentState.setHasUnpublishedChanges(true);
      await slidesQueryResult.refetch();
    },
  });

  const handleDeleteMultiChoiceQuestion = (id: string) => {
    deleteMultiChoiceQuestion.mutate(id);
  };

  const createQuestionGroupSlide = trpcAdmin.unitAssessment.createQuestionGroupSlide.useMutation({
    onSuccess: async (newSlide) => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      await slidesQueryResult.refetch();
      unitAssessmentState.setNewlyCreatedSlideId(newSlide.id);
    },
  });

  const handleAddQuestionGroup = async () => {
    await createQuestionGroupSlide.mutateAsync({
      assessmentId: unitAssessmentSnap.currentAssessmentId,
      title: 'Question Group',
      unitAssessmentVersionId: unitAssessmentSnap.draftVersionId ?? undefined,
    });
  };

  const handleAddSlide = async (slideKind: SlideKind) => {
    switch (slideKind) {
      case SlideKind.OPEN_QUESTION:
        unitAssessmentState.setEditorType(slideKind);
        unitAssessmentState.setEditorMode(EditorMode.CREATE);
        unitAssessmentState.setCurrentSlide(null);
        unitAssessmentState.openEditor();
        break;

      case SlideKind.QUESTION_GROUP:
        await handleAddQuestionGroup();
        break;

      case SlideKind.CONTENT:
        unitAssessmentState.setEditorType(slideKind);
        unitAssessmentState.setEditorMode(EditorMode.CREATE);
        unitAssessmentState.setCurrentSlide(null);
        unitAssessmentState.openEditor();
        break;

      case SlideKind.FILE_UPLOAD_PROMPT:
        unitAssessmentState.setEditorType(slideKind);
        unitAssessmentState.setEditorMode(EditorMode.CREATE);
        unitAssessmentState.setCurrentSlide(null);
        unitAssessmentState.openEditor();
        break;

      case SlideKind.FORM:
        unitAssessmentState.setEditorType(slideKind);
        unitAssessmentState.setEditorMode(EditorMode.CREATE);
        unitAssessmentState.setCurrentSlide(null);
        unitAssessmentState.openEditor();
        break;

      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <EditorLayout
      toolbar={{
        breadcrumbs: (
          <EditorBreadcrumbs
            crumbs={[
              { label: t`Units`, to: '/units' },
              { label: unitOfCompetency.code || unitOfCompetency.title, to: `/units/${unitId}` },
            ]}
            current={
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                <Trans>Assessment</Trans>
              </Text>
            }
          />
        ),
        status: (
          <EditorStatus
            published={{
              isPublished: assessment.lastPublishedVersion?.publishedAt != null,
              publishedAt: assessment.lastPublishedVersion?.publishedAt,
              hasUnpublishedChanges: unitAssessmentSnap.hasUnpublishedChanges,
            }}
          />
        ),
        actions: [
          { id: 'continue-generation', node: <ContinueGenerationButton unitId={unitId} /> },
          { id: 'publish', node: <AssessmentPublishButton /> },
        ],
      }}
      body={{
        kind: 'panes',
        children: (
          <FileUrlsProvider fileUrls={fileUrls}>
            <Stack
              px={pageContentPadding.x}
              py={pageContentPadding.y}
              spacing="6"
              w={unitAssessmentSnap.isEditorOpen ? '40%' : ['100%', '90%', '85%', '80%']}
              mx={unitAssessmentSnap.isEditorOpen ? '0' : 'auto'}
              borderRightWidth={unitAssessmentSnap.isEditorOpen ? '1px' : '0'}
              overflowY="auto"
              h="100%"
            >
              {/* The prominent heading is kept on the page (the breadcrumb carries
                the same "Assessment" label more compactly). */}
              <Text textStyle="3xl" fontWeight="medium">
                {unitOfCompetency.code} - {unitOfCompetency.title} <Trans>Assessment</Trans>
              </Text>

              <AssessmentSlides
                slides={slides}
                handleSlideDelete={handleSlideDelete}
                handleDeleteMultiChoiceQuestion={handleDeleteMultiChoiceQuestion}
                setSlides={setSlides}
                saveReorder={saveReorder}
              />

              <Flex
                direction="column"
                align="center"
                justify="center"
                h="80px"
                w="full"
                rounded="lg"
                border="3px dashed currentColor"
                color={'gray.200'}
              >
                <Menu>
                  <MenuButton
                    as={Button}
                    leftIcon={<Icon as={Plus} boxSize="24px" />}
                    variant="tertiary"
                  >
                    <Trans>Add Assessment Slide</Trans>
                  </MenuButton>
                  <MenuList shadow="lg" py="4" color={'gray.600'} px="3">
                    <>
                      <MenuItem
                        icon={getSlideIcon(SlideKind.CONTENT)}
                        onClick={async () => handleAddSlide(SlideKind.CONTENT)}
                      >
                        <Trans>Content</Trans>
                      </MenuItem>
                      <MenuDivider />
                    </>

                    <MenuItem
                      icon={getSlideIcon(SlideKind.OPEN_QUESTION)}
                      onClick={async () => handleAddSlide(SlideKind.OPEN_QUESTION)}
                    >
                      <Trans>Open Question</Trans>
                    </MenuItem>

                    <MenuItem
                      icon={getSlideIcon(SlideKind.FORM)}
                      onClick={() => handleAddSlide(SlideKind.FORM)}
                    >
                      <Trans>Interactive Form</Trans>
                    </MenuItem>

                    <MenuItem
                      icon={getSlideIcon(SlideKind.QUESTION_GROUP)}
                      onClick={async () => handleAddSlide(SlideKind.QUESTION_GROUP)}
                    >
                      <Trans>Question Group</Trans>
                    </MenuItem>
                    <MenuItem
                      icon={getSlideIcon(SlideKind.FILE_UPLOAD_PROMPT)}
                      onClick={async () => handleAddSlide(SlideKind.FILE_UPLOAD_PROMPT)}
                    >
                      <Trans>File upload prompt</Trans>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </Stack>
            <AssessmentSlideEditor refetch={slidesQueryResult.refetch} />
          </FileUrlsProvider>
        ),
      }}
    />
  );
});
